/**
 * TransactionCommand (ADR-009 §3.3): one history entry, one revision and one
 * actor for any number of intents.
 *
 * Intents are compiled in order against the document as the earlier intents
 * left it, so later intents can address layers created or moved by earlier
 * ones. The first rejected child (or failed compile) rolls every executed
 * child back in reverse and the whole transaction is rejected; the document
 * and history are then exactly as before.
 *
 * That guarantee rests on the native command contract: `execute` validates
 * fully before it mutates, and never throws or rejects once it has changed
 * the document. A command that fails part-way is not rolled back, because
 * its `undo` assumes a completed `execute`.
 */
import { contentLabel, type ActorRef, type FieldChange, type Intent, type LayerId, type RejectionCode } from '@maipo/agent-protocol';
import type { ActionStep } from '../core/action-types';
import { changed, noOp, rejected, retainedCommandBytes, type Command, type CommandExecution } from '../core/commands';
import { EMPTY_DAMAGE, mergeDamage, type DamageSet } from '../core/damage';
import { findLayer, type EditorDocument } from '../core/model';
import { IntentRejection, isDeferredStep, type AliasTable, type CompiledStep, type Footprint } from './registry';

export interface TxMeta {
  txId: string;
  actor: ActorRef;
  label: string;
  createdAt: string;
  baseRevision: number;
  intents: Intent[];
  footprint: Footprint;
  /** Field-level changes, filled in by the dispatcher after the commit. */
  journal: FieldChange[];
  aliases: Record<string, LayerId>;
  /** Set on a revert (ADR-009 §6.2): the transaction this one compensates. */
  reverts?: string;
}

export interface TransactionOptions {
  /** Put the user's layer selection back after execute (agents, unless they adopt selection). */
  restoreSelection: boolean;
  /**
   * Leave layer selection entirely to the native commands on execute, undo
   * and redo, exactly as if they had been executed directly. The UI's user
   * path uses it so selection behaviour does not change.
   */
  nativeSelection?: boolean;
  /**
   * Runs once every stage executed, before the transaction counts as
   * committed; a refusal rolls every child back (the dispatcher's
   * layer-count backstop for agents).
   */
  check?(document: EditorDocument): { code: RejectionCode; message: string } | null;
}

/** Builds the native steps for one intent against the current document. May throw IntentRejection. */
export interface TransactionStage {
  intentIndex: number;
  build(document: EditorDocument, aliases: AliasTable): CompiledStep[];
}

export interface TransactionRejectionInfo { code: RejectionCode; intentIndex: number; message: string; layer?: LayerId }

export function actorLabel(actor: ActorRef, label: string): string {
  const safe = contentLabel(label, 80);
  switch (actor.kind) {
    case 'agent': return `Agent · ${safe}`;
    case 'addon': return `Add-on · ${safe}`;
    case 'replay': return `Action · ${safe}`;
    case 'user': return safe;
  }
}

interface ExecutedChild { command: Command; damage: DamageSet }

export class TransactionCommand implements Command {
  readonly label: string;
  /** Set when execute() rejected; the transaction left no trace in the document. */
  rejection?: TransactionRejectionInfo;
  private children: ExecutedChild[] = [];
  private activeBefore: LayerId[] = [];
  private activeAfter: LayerId[] = [];
  private executed = false;
  private readonly aliases: AliasTable = new Map();
  private pins = 0;
  private disposeRequested = false;
  private released = false;

  constructor(
    readonly meta: TxMeta,
    private readonly stages: readonly TransactionStage[],
    private readonly options: TransactionOptions = { restoreSelection: true },
  ) {
    this.label = actorLabel(meta.actor, meta.label);
  }

  /** True once history evicted (disposed) this entry; a pinned entry keeps its children until unpinned. */
  get evicted(): boolean { return this.disposeRequested; }

  /** Keeps the children alive for a revert that can still undo or redo them. */
  pin(): void { this.pins += 1; }

  unpin(): void {
    this.pins = Math.max(0, this.pins - 1);
    if (this.pins === 0 && this.disposeRequested) this.release();
  }

  /** The command classes this transaction executed, in order (for revert eligibility). */
  childCommands(): readonly Command[] { return this.children.map((child) => child.command); }

  /** Undoes the children in reverse without touching the layer selection (revert, ADR-009 §6.2). */
  undoChildren(document: EditorDocument): void {
    for (const child of [...this.children].reverse()) child.command.undo(document);
  }

  /** Re-applies the children in order without touching the layer selection. */
  redoChildren(document: EditorDocument): void {
    for (const child of this.children) {
      if (child.command.redo) child.command.redo(document);
      else child.command.execute(document);
    }
  }

  /**
   * A user transaction of one native command records exactly as that command
   * did before the UI moved onto the dispatcher (a replayable step or a
   * marker); other transactions record as a marker.
   */
  toActionStep(): ActionStep | null {
    if (this.meta.actor.kind !== 'user' || this.children.length !== 1) return null;
    return this.children[0]?.command.toActionStep?.() ?? null;
  }

  /** Aliases resolved while the transaction executed. */
  resolvedAliases(): ReadonlyMap<string, LayerId> { return this.aliases; }

  get childCount(): number { return this.children.length; }

  retainedBytes(): number {
    let bytes = 256;
    for (const child of this.children) bytes += retainedCommandBytes(child.command);
    try { bytes += JSON.stringify(this.meta).length * 2; } catch { /* metadata is plain JSON */ }
    return bytes;
  }

  execute(document: EditorDocument): CommandExecution {
    if (this.executed) {
      this.redo(document);
      return changed(this.damage());
    }
    this.activeBefore = [...document.activeLayerIds];
    for (const stage of this.stages) {
      const failure = this.runStage(document, stage);
      if (failure) {
        this.rollback(document);
        this.rejection = failure;
        return rejected(`intent ${failure.intentIndex}: ${failure.message}`);
      }
    }
    const refused = this.options.check?.(document);
    if (refused) {
      this.rollback(document);
      this.rejection = { code: refused.code, intentIndex: Math.max(0, this.stages.length - 1), message: refused.message };
      return rejected(refused.message);
    }
    this.executed = true;
    this.meta.aliases = Object.fromEntries(this.aliases);
    if (this.children.length === 0) return noOp();
    if (this.options.nativeSelection) return changed(this.damage());
    if (this.options.restoreSelection) document.activeLayerIds = this.activeBefore.filter((id) => Boolean(findLayer(document.root, id)));
    this.activeAfter = [...document.activeLayerIds];
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    this.undoChildren(document);
    if (!this.options.nativeSelection) document.activeLayerIds = [...this.activeBefore];
  }

  redo(document: EditorDocument): void {
    this.redoChildren(document);
    if (!this.options.nativeSelection) document.activeLayerIds = [...this.activeAfter];
  }

  damage(): DamageSet {
    return this.children.length === 0 ? EMPTY_DAMAGE : mergeDamage(...this.children.map((child) => child.damage));
  }

  dispose(): void {
    this.disposeRequested = true;
    if (this.pins === 0) this.release();
  }

  private release(): void {
    if (this.released) return;
    this.released = true;
    for (const child of this.children) child.command.dispose?.();
  }

  private runStage(document: EditorDocument, stage: TransactionStage): TransactionRejectionInfo | null {
    let queue: CompiledStep[];
    try {
      queue = stage.build(document, this.aliases);
    } catch (error) {
      return this.failure(stage.intentIndex, error);
    }
    while (queue.length > 0) {
      const step = queue.shift() as CompiledStep;
      if (isDeferredStep(step)) {
        try {
          queue = [...step.deferred(document, this.aliases), ...queue];
        } catch (error) {
          return this.failure(stage.intentIndex, error);
        }
        continue;
      }
      let result: CommandExecution;
      try {
        result = step.execute(document);
      } catch (error) {
        step.dispose?.();
        return this.failure(stage.intentIndex, error);
      }
      if (result.outcome === 'rejected') {
        step.dispose?.();
        return { code: 'COMMAND_REJECTED', intentIndex: stage.intentIndex, message: result.reason };
      }
      if (result.outcome === 'no-op') {
        step.dispose?.();
        continue;
      }
      const childDamage = result.damage === EMPTY_DAMAGE && step.damage ? step.damage() : result.damage;
      this.children.push({ command: step, damage: childDamage });
    }
    return null;
  }

  private failure(intentIndex: number, error: unknown): TransactionRejectionInfo {
    if (error instanceof IntentRejection) {
      return { code: error.code, intentIndex, message: error.message, ...(error.layer === undefined ? {} : { layer: error.layer }) };
    }
    return { code: 'COMMAND_REJECTED', intentIndex, message: error instanceof Error ? error.message : 'Command failed.' };
  }

  private rollback(document: EditorDocument): void {
    for (const child of [...this.children].reverse()) {
      child.command.undo(document);
      child.command.dispose?.();
    }
    this.children = [];
    this.aliases.clear();
    document.activeLayerIds = [...this.activeBefore];
  }
}

/**
 * The compensating step of a revert (ADR-009 §6.2): undoes a committed
 * transaction's native commands as a new forward edit, and re-applies them
 * when the revert itself is undone. It pins the target so history eviction
 * cannot dispose children the revert still needs.
 */
export class InverseTransactionStep implements Command {
  readonly label: string;
  private disposed = false;

  constructor(private readonly target: TransactionCommand) {
    this.label = `Revert ${target.label}`;
    target.pin();
  }

  retainedBytes(): number { return 64; }

  // Selection after the compensation is whatever the native undos put back;
  // undoing the revert returns to the selection from just before it.
  private selectionBefore: LayerId[] = [];
  private selectionAfter: LayerId[] = [];

  execute(document: EditorDocument): CommandExecution {
    this.selectionBefore = [...document.activeLayerIds];
    this.target.undoChildren(document);
    this.selectionAfter = [...document.activeLayerIds];
    return changed(this.target.damage());
  }

  undo(document: EditorDocument): void {
    this.target.redoChildren(document);
    document.activeLayerIds = [...this.selectionBefore];
  }

  redo(document: EditorDocument): void {
    this.target.undoChildren(document);
    document.activeLayerIds = [...this.selectionAfter];
  }

  damage(): DamageSet { return this.target.damage(); }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.target.unpin();
  }
}
